import { ShipmentUnitEnum } from '@uturn/api/v1';
import { MetaDataContext } from '@uturn/portal/context';
import { isSelectableShippingLine } from '@uturn/portal/types/shipping-line.typeguard';
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Combobox,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	InputAddon,
} from '@uturn/ui-kit';
import { useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export function ContainerForm({ disabled }: { disabled?: boolean }) {
	const { shipmentMetaData, shipmentUnits } = useContext(MetaDataContext);
	const form = useFormContext();
	const { t } = useTranslation();

	return (
		<Card>
			<CardHeader>
				<CardTitle>
					{t('pages.lanes.create.form.container.title', 'Container')}
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				<FormField
					control={form.control}
					name="shipmentUnit"
					disabled={disabled}
					render={({ field }) => (
						<FormItem className="flex flex-col">
							<FormLabel className="required-asterix">
								{t('pages.lanes.create.form.container.type.label', 'Type')}
							</FormLabel>
							<Combobox
								defaultValue={field.value} // ShipmentUnitEnum.key
								onChange={field.onChange}
								options={Object.keys(ShipmentUnitEnum)
									.filter((unit) => shipmentUnits[unit] !== undefined)
									.map((key) => ({
										value: key,
										label: shipmentUnits[key].description,
									}))}
								className="w-full"
								disabled={field.disabled}
								placeholder={t(
									'pages.lanes.create.form.container.type.placeholder',
									'Select container type',
								).toString()}
								searchPlaceholder={t(
									'pages.lanes.create.form.container.type.dropdown.search',
									'Search for a container type',
								).toString()}
								emptyMessage={t(
									'pages.lanes.create.form.container.type.dropdown.no-results',
									'No container types',
								).toString()}
							/>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="cargoWeight.quantity"
					disabled={disabled}
					render={({ field }) => (
						<FormItem>
							<FormLabel className="required-asterix">
								{t(
									'pages.lanes.create.form.container.average-cargo-weight',
									'Average cargo weight',
								)}
							</FormLabel>
							<div className="relative">
								<Input type="number" {...field} />
								<InputAddon type="trailing">
									<span className="text-secondary-500 sm:text-sm">
										{t('general.shorthands.units.kilogram', 'kg')}
									</span>
								</InputAddon>
							</div>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="shippingLineId"
					disabled={disabled}
					render={({ field }) => (
						<FormItem className="flex flex-col">
							<FormLabel>
								{t(
									'pages.lanes.create.form.container.shipping-line.label',
									'Shipping line',
								)}
							</FormLabel>
							<Combobox
								defaultValue={field.value} // shippingLine.id
								onChange={field.onChange}
								options={shipmentMetaData?.shippingLines
									?.filter(isSelectableShippingLine)
									.map((line) => ({
										value: line.id,
										label: `${line.description} (${line.code})`,
									}))}
								className="w-full"
								disabled={field.disabled}
								placeholder={t(
									'pages.lanes.create.form.container.shipping-line.placeholder',
									'Select shipping line',
								).toString()}
								searchPlaceholder={t(
									'pages.lanes.create.form.container.shipping-line.dropdown.search',
									'Search for a shipping line',
								).toString()}
								emptyMessage={t(
									'pages.lanes.create.form.container.shipping-line.dropdown.no-results',
									'No shipping line found',
								).toString()}
							/>
							<FormMessage />
						</FormItem>
					)}
				/>
			</CardContent>
		</Card>
	);
}
